import { useEffect, useState } from 'react';
import axios from 'axios';
import { useProjectId } from '@client/store/project';
import { useConfig } from '@client/providers/ConfigProvider';
import { useScope } from '@features/scope/scope.store';
import { isNonProductionEnvironment } from '@shared/helpers/environment.helpers';
import { appendScopeQueryParamsToUrl } from '@shared/helpers/scope-query-params.helpers';
import { toSameOriginPath } from '@shared/helpers/url.helpers';
import type { ProjectID } from '@shared/models/project.model';

type GenerationKeyResponse = {
  generation_key: string;
  printUrl: string;
  browseUrl: string;
};

type MintedPrintPath = {
  projectId: ProjectID;
  path: string;
};

/**
 * Mints a report print link for the current project and returns it as a router
 * path, or `undefined` while unavailable.
 *
 * The link carries a time-limited generation key, so it must be minted before
 * it can be rendered as an anchor — mint while {@link enabled} to keep the key
 * fresh at click time. The key covers the project's active version, which is
 * what the PDF export renders too.
 *
 * Only available where the generation-key endpoint is: non-production.
 */
export const useReportPrintLink = ({
  enabled,
}: {
  enabled: boolean;
}): string | undefined => {
  const [config] = useConfig();
  const projectId = useProjectId();
  const scope = useScope();
  // The path is tied to the project it was minted for: a project switch must not
  // hand out the previous project's report until the new mint resolves.
  const [minted, setMinted] = useState<MintedPrintPath>();
  const isAvailable =
    enabled &&
    projectId != null &&
    isNonProductionEnvironment(config.environment);

  useEffect(() => {
    // Keys expire, so a dropped link must be re-minted rather than reused.
    if (!isAvailable) {
      setMinted(undefined);
      return;
    }
    let cancelled = false;

    void (async () => {
      try {
        const { data } = await axios.post<GenerationKeyResponse>(
          `projects/${projectId}/generation-key`,
        );
        if (cancelled) return;
        const path = toSameOriginPath(data.printUrl);
        setMinted(path ? { projectId, path } : undefined);
      } catch {
        if (!cancelled) setMinted(undefined);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAvailable, projectId]);

  if (!isAvailable) return undefined;
  if (minted?.projectId !== projectId) return undefined;
  return appendScopeQueryParamsToUrl(minted.path, scope);
};
